<script lang="ts">
	import {
		currentLot,
		defaultCatalogOptions,
		defaultMaterialOptions,
		mapFormControls
	} from '../utils/fakStores'
	import NullableDataWrapper from '#lib/utils/NullableDataWrapper.svelte'
	import { inputFieldFormats } from '$utils/formatters'
	import { t } from '$i18n/i18n'
	import { CalcMethod } from '$types/enums'
	import AutocompleteInputField from '#lib/autocomplete/AutocompleteInputField.svelte'
	import InputField from '#lib/molecules/InputField.svelte'
	import { vehicleCategories } from '#lib/atoms/vehicleCategoriesStore.js'
	import type { VehicleCategory } from '#lib/optimizer/vehicleCategory.js'
	import { AutocompleteConfig, type AutocompleteFieldConfig } from '$types/autocompleteFieldConfig'
	import {
		copyLot,
		updateMaterialPropertyComments,
		validateAndSaveLot,
		type Lot
	} from '#lib/map/types/lot.js'
	import type { Catalog } from '#lib/map/types/catalog.js'
	import type { Material } from '#lib/map/types/material.js'
	import { DefaultLotEditFieldsView } from '$types/lotEditFieldsView'
	import type { FontProps } from '$types/fontProps'
	import { DefaultRobotoFontProps } from '$types/fontProps'
	import PrimaryButton from '#lib/atoms/PrimaryButton.svelte'
	import TextArea, { textAreaHeights } from '#lib/atoms/TextArea.svelte'
	import Icon from '#lib/atoms/Icon.svelte'
	import iconClose from '@/icons/icon_expandable_closed.svg'
	import CatalogMaterialAutocomplete from './LotMaterialForm/CatalogMaterialAutocomplete.svelte'
	import { firstMaterialForCatalog } from './LotMaterialForm/catalogMaterial'

	const fontProps: FontProps = DefaultRobotoFontProps('0.875rem', '0.75rem')
	const LOT_FORM_ERROR_PATH_PREFIX = 'lot.'

	function initialCanEditLot(): boolean {
		const lot = $currentLot
		return lot ? lot.lotViewFieldsChangeMap.canEditLot : DefaultLotEditFieldsView().canEditLot
	}

	// let lotEditFieldsView: LotEditFieldsView
	$: lotEditFieldsView = $currentLot
		? $currentLot.lotViewFieldsChangeMap
		: DefaultLotEditFieldsView()

	let canNotEditLot = !initialCanEditLot()

	$: if ($currentLot) {
		canNotEditLot = !lotEditFieldsView.canEditLot
	}

	let vehicleCategoryAcConfig: AutocompleteFieldConfig = AutocompleteConfig(
		'id',
		'label',
		true,
		!initialCanEditLot()
	)
	$: vehicleCategoriesValue = $currentLot?.vehicleCategories.map((c) => c.id)

	function onVehicleCategoriesChange(
		selection: string | number | object | unknown[] | null | undefined
	) {
		if (!$currentLot) return
		// multi-select onChange is always array-or-null; other union members exist for single-mode
		$currentLot.vehicleCategories = Array.isArray(selection) ? (selection as VehicleCategory[]) : []
		triggerLotValidationAndSave()
	}

	let materialCommentsOpen = true
	let materialCommentsRequiredOpen = true

	/**
	 * the shared CatalogMaterial owns the material value (auto-selects on catalog change, clears on
	 * unselect); here we just reset the lot-specific material properties + comments and persist.
	 */
	function onCatalogChanged() {
		if (!$currentLot) return
		$currentLot.materialProperties = ''
		materialCommentsOpen = true
		materialCommentsRequiredOpen = true
		updateMaterialPropertyComments()
		triggerLotValidationAndSave()
	}

	function clearMaterial() {
		if (!$currentLot) return
		$currentLot.materialProperties = ''
		materialCommentsOpen = true
		materialCommentsRequiredOpen = true
		updateMaterialPropertyComments()
		triggerLotValidationAndSave()
	}

	function onMaterialChanged() {
		updateMaterialPropertyComments()
		triggerLotValidationAndSave()
	}

	function triggerLotValidationAndSave() {
		if ($currentLot && $currentLot.id !== 'new') {
			validateAndSaveLot(copyLot($currentLot))
		}
	}

	// keyed by object identity, not id (new lots share 'new') nor a flag (a clear keeps the same object)
	let defaultedLot: Lot | null = null

	function applyNewLotDefaults(
		lot: Lot | null,
		catalogs: Catalog[],
		materialsByCatalog: Record<string, Material[]>
	) {
		if (!lot || lot.id !== 'new' || lot === defaultedLot || catalogs.length === 0) return
		defaultedLot = lot // set first: currentLot.set below re-enters this reactive
		if (!lot.catalog) lot.catalog = catalogs[0]
		if (lot.catalog && !lot.material) {
			const material = firstMaterialForCatalog(lot.catalog, materialsByCatalog)
			if (material) {
				lot.material = material
				onMaterialChanged()
			}
		}
		currentLot.set(lot) // emit the in-place mutations so bound CatalogMaterial updates
	}
	// args passed so the legacy `$:` tracks them as dependencies (it only tracks vars in the statement)
	$: applyNewLotDefaults($currentLot, $defaultCatalogOptions, $defaultMaterialOptions)

	const gridTemplateAreas =
		'"catalog material"\
    "materialProperties materialProperties"\
    "materialPropertiesComments materialPropertiesComments"\
    "materialPropertiesCommentsRequired materialPropertiesCommentsRequired"\
    "volume dailyCapacity"\
    "vehicleCategories vehicleCategories"\
    "pricePerTon pricePerTon"\
    "kickbackInPercent kickbackPerTon"'

	const gridTemplateAreasNoMaterial =
		'"catalog material"\
    "materialProperties materialProperties"\
    "volume dailyCapacity"\
    "vehicleCategories vehicleCategories"\
    "pricePerTon pricePerTon"\
    "kickbackInPercent kickbackPerTon"'
</script>

<NullableDataWrapper bind:data={$currentLot}>
	{#if $currentLot}
		<div class="lot-form-container">
			<div class="lot-material-data">
				<div
					style="grid-template-areas: {$currentLot.material
						? gridTemplateAreas
						: gridTemplateAreasNoMaterial}"
				>
					<CatalogMaterialAutocomplete
						bind:catalog={$currentLot.catalog}
						bind:material={$currentLot.material}
						catalogOptions={$defaultCatalogOptions}
						materialDefaultsByCatalog={$defaultMaterialOptions}
						disabled={canNotEditLot}
						saving={$mapFormControls.saving}
						{fontProps}
						errorPathPrefix={LOT_FORM_ERROR_PATH_PREFIX}
						catalogGridArea="catalog"
						materialGridArea="material"
						onCatalogChange={onCatalogChanged}
						onCatalogClear={onCatalogChanged}
						onMaterialChange={onMaterialChanged}
						onMaterialClear={clearMaterial}
					/>
					<div style="grid-area: materialProperties">
						<PrimaryButton
							on:click={() => dispatchEvent(new CustomEvent('openMaterialProperties'))}
							label={$t(
								canNotEditLot
									? 'UI.map.lot.button.materialProperties.readOnly'
									: 'UI.map.lot.button.materialProperties'
							)}
							fullWidth
							disabled={!$currentLot || !$currentLot.material}
						/>
					</div>
					{#if $currentLot.material}
						<div style="grid-area: materialPropertiesComments; overflow: hidden;">
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div
								on:click={() => (materialCommentsOpen = !materialCommentsOpen)}
								class="material-comments-container hover-pointer"
							>
								<span>{$t('Lot.materialPropertiesComments')}</span>
								<div
									style="transform: rotate({materialCommentsOpen
										? '90deg'
										: '0'}); transition: transform .4s ease-out;"
								>
									<Icon src={iconClose} size={20} clickable />
								</div>
							</div>
							<div
								class="divider"
								style="background: {materialCommentsOpen ? '#fff' : '#d9d9d9'};"
							/>
							<div
								style="height: {materialCommentsOpen
									? '8.1875'
									: '0'}rem; transition: height .5s ease-out;"
							>
								<TextArea
									bind:text={$currentLot.materialPropertiesComment}
									marginTop="0"
									labelGap="0"
									readonly={!lotEditFieldsView.canEditLot}
									height={textAreaHeights.MID}
									on:focusout={triggerLotValidationAndSave}
								/>
							</div>
						</div>
						<div style="grid-area: materialPropertiesCommentsRequired; overflow: hidden;">
							<!-- svelte-ignore a11y-click-events-have-key-events -->
							<div
								on:click={() => (materialCommentsRequiredOpen = !materialCommentsRequiredOpen)}
								class="material-comments-container hover-pointer"
							>
								<span>{$t('Lot.materialPropertiesCommentsWithoutMandatory')}</span>
								<div
									style="transform: rotate({materialCommentsRequiredOpen
										? '90deg'
										: '0'}); transition: transform .4s ease-out;"
								>
									<Icon src={iconClose} size={20} clickable />
								</div>
							</div>
							<div
								class="divider"
								style="background: {materialCommentsRequiredOpen ? '#fff' : '#d9d9d9'};"
							/>
							<div
								style="height: {materialCommentsRequiredOpen
									? '8.1875'
									: '0'}rem; transition: height .5s ease-out;"
							>
								<TextArea
									bind:text={$currentLot.materialPropertiesCommentWithoutOptional}
									marginTop="0"
									labelGap="0"
									readonly={!lotEditFieldsView.canEditLot}
									height={textAreaHeights.MID}
									on:focusout={triggerLotValidationAndSave}
								/>
							</div>
						</div>
					{/if}
					<div style="grid-area: volume">
						<InputField
							bind:value={$currentLot.volume}
							{fontProps}
							unit="T"
							id="lotVolume"
							format={inputFieldFormats.FULL}
							placeholder={$t('UI.map.lot.volume.ph')}
							readOnly={!lotEditFieldsView.canEditLot}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'volume'}
							on:focusout={triggerLotValidationAndSave}
							isNumeric
							required
						/>
					</div>
					<div style="grid-area: dailyCapacity">
						<InputField
							bind:value={$currentLot.dailyCapacity}
							{fontProps}
							unit="T"
							format={inputFieldFormats.FULL}
							placeholder={$t('UI.map.lot.dailyCapacity.ph')}
							readOnly={!lotEditFieldsView.canEditLot}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'dailyCapacity'}
							id="lotDailyCapacity"
							on:focusout={triggerLotValidationAndSave}
							isNumeric
						/>
					</div>
					<div class="vehicle-categories-container">
						<AutocompleteInputField
							value={vehicleCategoriesValue}
							bind:disabled={canNotEditLot}
							options={$vehicleCategories}
							dropdownMaxHeight="6.25rem"
							{fontProps}
							autocompleteConfig={vehicleCategoryAcConfig}
							label={$t('UI.lot.vehicleCategories.label')}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'vehicleCategories'}
							height="100%"
							onChange={onVehicleCategoriesChange}
						/>
					</div>
					<div style="grid-area: pricePerTon">
						<InputField
							bind:value={$currentLot.grossPricePerTon}
							{fontProps}
							unit="€"
							forcedDecimalPlaces={2}
							format={inputFieldFormats.FULL}
							placeholder={$t('UI.map.lot.pricePerTon.ph')}
							readOnly={$currentLot.calcMethod === CalcMethod.REQUEST_OFFER ||
								!lotEditFieldsView.canEditLot}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'grossPricePerTon'}
							on:focusout={triggerLotValidationAndSave}
							isNumeric
						/>
					</div>
					<div style="grid-area: kickbackInPercent">
						<InputField
							bind:value={$currentLot.kickbackInPercent}
							{fontProps}
							unit="%"
							forcedDecimalPlaces={2}
							format={inputFieldFormats.FULL}
							placeholder={$t('UI.map.lot.kickbackInPercent.ph')}
							readOnly={!lotEditFieldsView.canEditLot}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'kickbackInPercent'}
							on:focusout={triggerLotValidationAndSave}
							isNumeric
						/>
					</div>
					<div style="grid-area: kickbackPerTon">
						<InputField
							bind:value={$currentLot.kickbackPerTon}
							{fontProps}
							unit="€"
							forcedDecimalPlaces={2}
							format={inputFieldFormats.FULL}
							readOnly={!lotEditFieldsView.canEditLot}
							placeholder={$t('UI.map.lot.kickbackPerTon.ph')}
							errorPath={LOT_FORM_ERROR_PATH_PREFIX + 'kickbackPerTon'}
							on:focusout={triggerLotValidationAndSave}
							isNumeric
						/>
					</div>
				</div>
			</div>
		</div>
	{/if}
</NullableDataWrapper>

<style lang="scss">
	@import '$styles/global';
	@import '$styles/fak';

	.lot-form-container {
		padding-left: 1.313rem;
		padding-right: 1.313rem;
		padding-bottom: 1.5rem;
		width: 100%;
		max-width: 450px;

		@include flex-col($justify: flex-start);

		& > span,
		& > div > span {
			@include roboto-font(1rem, 400, 0.875rem, black);

			align-self: start;
			padding-top: 1em;
			padding-bottom: 0.875em;
		}
	}

	.lot-material-data {
		width: 100%;
		padding-top: 0.4rem;

		@include flex-col(0.625rem, $alignment: flex-start);

		& > div {
			width: 100%;
			display: grid;
			grid-template-columns: repeat(2, minmax(0, 1fr));
			gap: 0.938rem 0.625rem;
		}
	}

	.material-comments-container {
		display: flex;
		justify-content: space-between;
		align-items: center;

		& > span {
			@include roboto-font(normal, 400, 0.875rem);
		}
	}

	.vehicle-categories-container {
		grid-area: vehicleCategories;

		& :global(.sv-dropdown) {
			position: static;
		}
	}

	.divider {
		height: 1px;
		margin-top: 0.5rem;
		border-radius: 50%;
		transition: background 0.3s ease-out;
	}
</style>
